import { useAuthStore } from '@/store/authStore'
import { auth } from '@/firebase'
import { auditLogger } from '@/utils/auditLogger'
import {
  canViewTestingAccommodationsPage,
  canViewTeacherAttendanceLog
} from '@/config/testingAccommodations'
import { canViewTestingCalendar } from '@/config/testingCalendar'
import {
  parseSchoolQueryParam,
  routeLocationWithSchool,
  studentsRouteLocation,
  getSafeRedirectPath,
  appPathToLocation,
  stashPostLoginRedirect,
  REDIRECT_QUERY_PARAM
} from '@/utils/schoolRoutes'
import { DISTRICT_DASHBOARD_ROLES } from '@/config/roles'
import { isUsageAnalyticsOwner } from '@/config/analytics'
import { usageAnalytics } from '@/utils/usageAnalytics'
import { useSchoolStore } from '@/store/schoolStore'
import { areEvaluationsEnabled } from '@/utils/environment'

/**
 * Build a /login navigation target that remembers where the user was headed,
 * so a deep link (e.g. a feedback form) survives the sign-in round-trip.
 * Skips the param when the destination is the login page or otherwise unsafe.
 * @param {import('vue-router').RouteLocationNormalized} to
 */
function loginLocationFor(to) {
  const dest = getSafeRedirectPath(to?.fullPath)
  if (!dest) return { path: '/login' }
  // Keep a copy in sessionStorage so a Google full-page redirect still finds
  // the feedback (or other) deep link if ?redirect= is stripped on return.
  stashPostLoginRedirect(dest)
  return { path: '/login', query: { [REDIRECT_QUERY_PARAM]: dest } }
}

/**
 * Sync ?school= with schoolStore for multi-site UX. Returns a redirect location
 * when the URL is missing the param, or null when navigation may proceed.
 * @param {import('vue-router').RouteLocationNormalized} to
 * @returns {Promise<object|null>}
 */
export async function resolveSchoolRouteRedirect(to) {
  if (!to.meta.requiresAuth || to.meta.schoolContext === false) return null

  const schoolStore = useSchoolStore()
  const fromUrl = parseSchoolQueryParam(to.query)
  await schoolStore.loadSchools({ fromUrl: fromUrl || undefined })

  const active = schoolStore.currentSchoolId
  if (!schoolStore.isMultiSchool || !active) return null

  // Replace missing, stale, or out-of-scope ?school= (e.g. after switching users).
  if (fromUrl !== active) {
    return routeLocationWithSchool(to, active)
  }
  return null
}

export const authGuard = async (to, from, next) => {
  const authStore = useAuthStore()

  // Wait for auth to initialize
  while (authStore.isLoading) {
    await new Promise((resolve) => setTimeout(resolve, 50))
  }

  if (!authStore.currentUser) {
    // Log unauthorized access attempt
    await auditLogger.logSystemAccess('unauthorized_access_attempt', {
      attemptedRoute: to.path,
      fromRoute: from.path,
      success: false
    })

    next(loginLocationFor(to))
  } else if (!authStore.currentUser.role) {
    console.log(
      `User ${authStore.currentUser.uid} exists in Auth but has no role - redirecting to login for role assignment`
    )

    // Log incomplete user setup
    await auditLogger.logSystemAccess('incomplete_user_setup', {
      userId: authStore.currentUser.uid,
      userEmail: authStore.currentUser.email,
      attemptedRoute: to.path,
      reason: 'no_role_assigned'
    })

    next('/login')
  } else {
    next()
  }
}

async function redirectToStudents(next) {
  const schoolStore = useSchoolStore()
  await schoolStore.loadSchools()
  return next(studentsRouteLocation(schoolStore.currentSchoolId))
}

/** Owner-only surfaces (pilot usage analytics). Not a role — an email
 *  allow-list, so school/district admins are kept out too. */
function isOwnerOnlyRouteAllowed(to, user) {
  if (!to.meta.usageAnalyticsOwnerOnly) return true
  const allowed = isUsageAnalyticsOwner(user)
  if (!allowed) {
    // Visible in devtools so the owner can see EXACTLY which email the guard
    // evaluated and add it to ANALYTICS_OWNER_EMAILS — no guessing.
    console.warn(
      `[usage-analytics] owner check failed for email "${user?.email ?? '(none)'}". ` +
        'Add it to ANALYTICS_OWNER_EMAILS in src/config/analytics.js + firestore.rules and redeploy.'
    )
  }
  return allowed
}

/** Eval-gated routes (meta.requiresEvaluations) — hidden while the feature
 *  flag is off (school production until the EV view ships). */
function isEvaluationsRouteAllowed(to) {
  if (!to.meta.requiresEvaluations) return true
  return areEvaluationsEnabled()
}

/** Routes that allow testingAccess users outside allowedRoles (e.g. proctors). */
function isRouteRoleAllowed(to, user) {
  if (!to.meta.allowedRoles) return true
  if (to.meta.allowedRoles.includes(user.role)) return true
  if (!to.meta.allowTestingAccess) return false
  if (to.path === '/testing-calendar') return canViewTestingCalendar(user)
  if (to.path === '/testing-accommodations') return canViewTestingAccommodationsPage(user)
  if (to.path === '/testing-attendance') return canViewTeacherAttendanceLog(user)
  return user.testingAccess === true
}

// District-tier roles whose home page is the district dashboard (same set the
// /district route allows). Other roles (including plain `admin`) keep
// /students as home.
const DISTRICT_HOME_ROLES = DISTRICT_DASHBOARD_ROLES

/**
 * Where a denied route should go. District Coordinator cannot open /students,
 * so sending them there makes the guard redirect forever and freezes the tab.
 */
export function fallbackPathForDeniedRoute(user) {
  const role = user?.role
  if (DISTRICT_HOME_ROLES.includes(role)) return '/district'
  if (
    user?.allSchools === true &&
    ['case_manager', 'case_manager_full', 'speech_case_manager'].includes(role)
  ) {
    return '/my-caseload'
  }
  return '/students'
}

async function redirectAway(to, next) {
  const authStore = useAuthStore()
  const path = fallbackPathForDeniedRoute(authStore.currentUser)
  if (to.path === path) {
    console.warn(`Role ${authStore.currentUser?.role} is not allowed on ${to.path}`)
    return next(false)
  }
  if (path === '/students') return redirectToStudents(next)
  return next({ path, replace: true })
}

/**
 * Role-aware home destination for the bare "/" route: district-tier users land
 * on the district dashboard and pick a school from there; everyone else goes
 * to the students page with their active school. The district decision is
 * deliberately role-only (no registry read) so it cannot be skipped by a slow
 * or failed schools fetch during login.
 */
async function resolveHomeLocation() {
  const authStore = useAuthStore()
  const user = authStore.currentUser
  const role = user?.role
  if (DISTRICT_HOME_ROLES.includes(role)) {
    return { path: '/district' }
  }

  // District case manager: a school-scoped CM flagged allSchools lands on their
  // cross-school caseload page (role-only check, like the district decision, so
  // a slow schools fetch can't skip it).
  if (
    user?.allSchools === true &&
    ['case_manager', 'case_manager_full', 'speech_case_manager'].includes(role)
  ) {
    return { path: '/my-caseload' }
  }

  const schoolStore = useSchoolStore()
  await schoolStore.loadSchools()
  return studentsRouteLocation(schoolStore.currentSchoolId)
}

// Dedupe consecutive views of the same route: the ?school= sync redirect
// (resolveSchoolRouteRedirect) navigates twice for one user-visible page load.
let lastTrackedPage = { name: null, at: 0 }
function trackUsagePageView(to) {
  if (!to.meta.requiresAuth) return
  const now = Date.now()
  if (to.name === lastTrackedPage.name && now - lastTrackedPage.at < 2000) return
  lastTrackedPage = { name: to.name, at: now }
  usageAnalytics.trackPageView(to.name)
}

export const setupGuards = (router) => {
  // Global navigation guard to check authentication
  router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore()

    // Login page: forward already-authenticated users to their home page
    // (district dashboard for district-tier roles, students otherwise).
    // Anyone else gets the login screen immediately.
    if (to.path === '/login') {
      if (!authStore.isLoading && authStore.currentUser?.role) {
        // Honor a saved deep link (e.g. feedback form) captured before sign-in.
        // Must split query/hash out of the path — Vue Router ignores them in
        // `{ path: '/feedback/x?school=y' }`.
        const dest = appPathToLocation(to.query?.[REDIRECT_QUERY_PARAM])
        if (dest) return next({ ...dest, replace: true })
        return next({ ...(await resolveHomeLocation()), replace: true })
      }
      return next()
    }

    // Only check auth for routes that require it
    if (!to.meta.requiresAuth) {
      return next()
    }

    // Quick check - if auth is already loaded and user exists, allow immediately
    if (!authStore.isLoading && authStore.currentUser && authStore.currentUser.role) {
      // Bare "/" resolves to a role-aware home page
      if (to.name === 'Home') {
        return next({ ...(await resolveHomeLocation()), replace: true })
      }
      // Quick role check
      if (to.meta.allowedRoles && !isRouteRoleAllowed(to, authStore.currentUser)) {
        console.log('🚪 User role not allowed for route - redirecting home')
        return redirectAway(to, next)
      }
      if (!isEvaluationsRouteAllowed(to)) {
        return redirectAway(to, next)
      }
      if (!isOwnerOnlyRouteAllowed(to, authStore.currentUser)) {
        return redirectAway(to, next)
      }
      const schoolRedirect = await resolveSchoolRouteRedirect(to)
      if (schoolRedirect) return next({ ...schoolRedirect, replace: true })
      return next()
    }

    // Profile restore can still be in flight. Sending the user to login while
    // it is running looks like a logout on refresh.
    const waitForAuth = async (ms) => {
      const steps = Math.ceil(ms / 50)
      for (let i = 0; i < steps && authStore.isLoading; i++) {
        await new Promise((resolve) => setTimeout(resolve, 50))
      }
    }
    await waitForAuth(30000)
    if (authStore.isLoading && auth.currentUser) await waitForAuth(30000)

    if (authStore.isLoading && !auth.currentUser) {
      console.log('🚪 Auth loading timeout - redirecting to login')
      return next(loginLocationFor(to))
    }

    // Firebase still has a session. A missing profile in the store is a slow
    // or failed read, not a signed-out user.
    if (!authStore.currentUser && auth.currentUser) {
      console.warn('Sign-in is still in this browser; staying on the page')
      return next()
    }

    // If no user is authenticated, redirect to login (remember the destination)
    if (!authStore.currentUser) {
      console.log('🚪 No authenticated user - redirecting to login')
      return next(loginLocationFor(to))
    }

    // If user has no role, redirect to login
    if (!authStore.currentUser.role) {
      console.log('🚪 User has no role - redirecting to login')
      return next('/login')
    }

    // Bare "/" resolves to a role-aware home page
    if (to.name === 'Home') {
      return next({ ...(await resolveHomeLocation()), replace: true })
    }

    // Check role permissions
    if (to.meta.allowedRoles && !isRouteRoleAllowed(to, authStore.currentUser)) {
      console.log('🚪 User role not allowed for route - redirecting home')
      return redirectAway(to, next)
    }
    if (!isEvaluationsRouteAllowed(to)) {
      return redirectAway(to, next)
    }
    if (!isOwnerOnlyRouteAllowed(to, authStore.currentUser)) {
      return redirectAway(to, next)
    }

    const schoolRedirect = await resolveSchoolRouteRedirect(to)
    if (schoolRedirect) return next({ ...schoolRedirect, replace: true })

    // Allow access
    next()
  })

  console.log('Router guards setup completed')

  // Safety net: student menu locks body scroll while open; clear on every nav.
  router.afterEach((to) => {
    document.body.style.overflow = ''
    trackUsagePageView(to)
  })
}
